import { Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, Switch, Typography, useTheme } from '@mui/material'
import React, { useState } from 'react'
import { ScheduleConfig } from '../../Model/PlaylistModel'

type Props = {
  open: boolean
  initial?: ScheduleConfig
  onClose: () => void
  onSave: (schedule: ScheduleConfig | undefined) => void
}

const DAY_LABELS = ['Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb', 'Dom']

function defaultSchedule(): ScheduleConfig {
  return { days: [1, 2, 3, 4, 5, 6, 7], startTime: '08:00', endTime: '22:00' }
}

export default function ScheduleModal({ open, initial, onClose, onSave }: Props) {
  const theme = useTheme()
  const [alwaysShow, setAlwaysShow] = useState(!initial)
  const [config, setConfig] = useState<ScheduleConfig>(initial ?? defaultSchedule())

  // Sync quando o item selecionado muda
  React.useEffect(() => {
    setAlwaysShow(!initial)
    setConfig(initial ?? defaultSchedule())
  }, [initial])

  function handleToggleAlways(enabled: boolean) {
    setAlwaysShow(enabled)
    if (enabled) setConfig(defaultSchedule())
  }

  function toggleDay(day: number) {
    setConfig(prev => {
      const days = prev.days.includes(day)
        ? prev.days.filter(d => d !== day)
        : [...prev.days, day].sort((a, b) => a - b)
      return { ...prev, days }
    })
  }

  function handleSave() {
    onSave(alwaysShow ? undefined : config)
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>Agendamento</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, pt: 1 }}>
        <FormControlLabel
          control={<Switch checked={alwaysShow} onChange={(_, v) => handleToggleAlways(v)} />}
          label="Sempre exibir"
        />

        {!alwaysShow && (
          <>
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                Dias da semana
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                {DAY_LABELS.map((label, i) => {
                  const day = i + 1
                  const selected = config.days.includes(day)
                  return (
                    <Chip
                      key={day}
                      label={label}
                      size="small"
                      onClick={() => toggleDay(day)}
                      color={selected ? 'primary' : 'default'}
                      sx={{ cursor: 'pointer', fontWeight: selected ? 700 : 400 }}
                    />
                  )
                })}
              </Box>
            </Box>

            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
              <Box sx={{ flex: 1 }}>
                <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>De</Typography>
                <input
                  type="time"
                  value={config.startTime}
                  onChange={e => setConfig(prev => ({ ...prev, startTime: e.target.value }))}
                  style={{
                    width: '100%', padding: '8px 10px', borderRadius: 8,
                    border: `1px solid ${theme.palette.divider}`,
                    backgroundColor: theme.palette.background.paper,
                    color: theme.palette.text.primary,
                    fontSize: '0.875rem', outline: 'none', boxSizing: 'border-box',
                  }}
                />
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>Até</Typography>
                <input
                  type="time"
                  value={config.endTime}
                  onChange={e => setConfig(prev => ({ ...prev, endTime: e.target.value }))}
                  style={{
                    width: '100%', padding: '8px 10px', borderRadius: 8,
                    border: `1px solid ${theme.palette.divider}`,
                    backgroundColor: theme.palette.background.paper,
                    color: theme.palette.text.primary,
                    fontSize: '0.875rem', outline: 'none', boxSizing: 'border-box',
                  }}
                />
              </Box>
            </Box>
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} color="inherit">Cancelar</Button>
        <Button onClick={handleSave} variant="contained">Salvar</Button>
      </DialogActions>
    </Dialog>
  )
}
